import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Check, Instagram, Facebook } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow, Button } from "../components/primitives";
import { Seo } from "../components/Seo";
import { api } from "../lib/api";

const inputCls =
  "w-full bg-transparent border-b border-line py-3 text-ink placeholder:text-ink-soft/50 focus:outline-none focus:border-gold transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setDone(true);
    } catch {
      toast.error("Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Contact" description="Get in touch with Glam Wagon. Ask about services, service areas, partnerships or the mission." />
      <section className="pt-40 pb-24 max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-16" data-testid="contact-section">
        <div className="md:col-span-5">
          <Reveal>
            <Eyebrow>Get in touch</Eyebrow>
            <h1 className="font-serif text-5xl sm:text-6xl leading-[1] tracking-tight mt-6 text-ink">Let's talk.</h1>
            <p className="mt-6 text-lg text-ink-soft leading-relaxed">
              Whether it's a booking question, a partnership idea, or you simply believe in what we're
              building — we'd love to hear from you.
            </p>
            <div className="mt-10 space-y-5">
              <a href="mailto:hello@glamwagon.co" className="flex items-center gap-4 text-ink hover:text-gold transition-colors" data-testid="contact-email">
                <span className="w-10 h-10 rounded-full border border-line flex items-center justify-center text-gold"><Mail size={18} /></span>
                hello@glamwagon.co
              </a>
              <div className="flex items-center gap-4 text-ink">
                <span className="w-10 h-10 rounded-full border border-line flex items-center justify-center text-gold"><MapPin size={18} /></span>
                Mobile — we come to you within our service area
              </div>
              <div className="flex gap-3 pt-2">
                {[Instagram, Facebook].map((Icon, i) => (
                  <a key={Icon.displayName || i} href="#" aria-label="social" className="p-2.5 rounded-full border border-line text-ink hover:border-gold hover:text-gold transition-colors" data-testid={`contact-social-${i}`}>
                    <Icon size={18} />
                  </a>
                ))}
              </div>
            </div>
            <div className="mt-8 rounded-2xl overflow-hidden border border-line" data-testid="contact-map">
              <iframe
                title="Service area map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-122.55%2C37.70%2C-122.35%2C37.83&layer=mapnik"
                className="w-full h-56 grayscale contrast-110"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-7">
          {done ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="contact-success" className="rounded-3xl border border-line bg-surface p-12 text-center h-full flex flex-col justify-center">
              <div className="w-14 h-14 rounded-full bg-gold text-white flex items-center justify-center mx-auto"><Check size={26} /></div>
              <h2 className="font-serif text-3xl mt-6 text-ink">Message received.</h2>
              <p className="mt-3 text-ink-soft">Thank you for reaching out. We'll get back to you soon.</p>
            </motion.div>
          ) : (
            <Reveal delay={0.1}>
              <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-line bg-surface p-8 lg:p-12 space-y-7">
                <div className="grid sm:grid-cols-2 gap-7">
                  <input required placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name" />
                  <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
                </div>
                <input placeholder="Subject (optional)" value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject" />
                <textarea required rows={5} placeholder="Your message" value={form.message} onChange={set("message")} className={inputCls} data-testid="contact-message" />
                <Button type="submit" disabled={loading} data-testid="contact-submit">
                  {loading ? "Sending…" : "Send message"}
                </Button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
